import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, TextField, IconButton, Avatar,
    Chip, Select, MenuItem, Menu, Alert, InputAdornment,
    Card, CardContent, Tooltip, Skeleton,
} from "@mui/material";
import SearchIcon          from "@mui/icons-material/Search";
import MoreVertIcon        from "@mui/icons-material/MoreVert";
import KeyboardArrowLeft   from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight  from "@mui/icons-material/KeyboardArrowRight";
import ClearIcon           from "@mui/icons-material/Clear";
import EmailOutlinedIcon   from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon   from "@mui/icons-material/PhoneOutlined";
import OpenInNewIcon       from "@mui/icons-material/OpenInNew";
import PeopleRoundedIcon   from "@mui/icons-material/PeopleRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon   from "@mui/icons-material/CancelRounded";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";
import { encryptId } from "../helper/idCodec.js";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B",
    surface: "#FFFFFF",
};

// Avatar background colours derived from name
const AVATAR_PALETTE = [
    ["#E0F4F5","#007C84"], ["#FFF0E0","#FF7900"], ["#EEF2FF","#4F46E5"],
    ["#D1FAE5","#059669"], ["#FFE4E6","#E11D48"], ["#FEF3C7","#D97706"],
];
const avatarColor = (name = "") => {
    const idx = name.charCodeAt(0) % AVATAR_PALETTE.length;
    return AVATAR_PALETTE[idx];
};

const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    return parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : (parts[0]?.[0] ?? "?").toUpperCase();
};

// ── Stat pill ─────────────────────────────────────────────────────────────
const StatPill = ({ icon, label, value, color, bg }) => (
    <Box sx={{
        display: "flex", alignItems: "center", gap: 1.2,
        bgcolor: bg, border: `1px solid ${color}22`,
        borderRadius: "12px", px: 2, py: 1,
    }}>
        {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
        <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1.1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {value}
            </Typography>
            <Typography sx={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>{label}</Typography>
        </Box>
    </Box>
);

// ── Filter pill button ────────────────────────────────────────────────────
const FilterBtn = ({ label, active, count, color, bg, onClick }) => (
    <Box onClick={onClick} sx={{
        display: "flex", alignItems: "center", gap: 0.8,
        px: 1.8, py: 0.7, borderRadius: "10px", cursor: "pointer",
        bgcolor: active ? bg : "transparent",
        border: `1.5px solid ${active ? color + "55" : T.border}`,
        color: active ? color : T.muted,
        fontWeight: active ? 700 : 500, fontSize: 13,
        transition: "all 0.15s",
        "&:hover": { bgcolor: bg, color },
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
        {label}
        <Box component="span" sx={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            minWidth: 20, height: 20, borderRadius: "6px", fontSize: 11, fontWeight: 700,
            bgcolor: active ? color : T.border,
            color: active ? "#fff" : T.muted, px: "4px",
        }}>{count}</Box>
    </Box>
);

// ── Owner card ────────────────────────────────────────────────────────────
const OwnerCard = ({ user, onViewDetails, onMenuOpen, menuAnchor, menuOpen, onMenuClose, onStatusChange }) => {
    const [bg, fg] = avatarColor(user.name);
    const isActive = user.status === "Active";
    return (
        <Card sx={{
            borderRadius: "16px", border: `1px solid ${T.border}`,
            boxShadow: "0 1px 4px rgba(15,23,42,0.06)",
            transition: "transform 0.18s, box-shadow 0.18s",
            "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,124,132,0.12)" },
            display: "flex", flexDirection: "column", height: "100%",
        }}>
            {/* Top accent stripe */}
            <Box sx={{ height: 3, background: `linear-gradient(90deg, ${T.teal}, ${T.tealLt})` }} />
            <CardContent sx={{ p: 2.5, flexGrow: 1, "&:last-child": { pb: 2.5 } }}>
                {/* Header row */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Avatar
                        src={user.avatar}
                        alt={user.name}
                        sx={{ width: 52, height: 52, bgcolor: bg, color: fg, fontWeight: 800, fontSize: 18 }}
                    >
                        {!user.avatar && getInitials(user.name)}
                    </Avatar>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Chip
                            label={user.status}
                            size="small"
                            sx={{
                                fontWeight: 700, fontSize: 11, borderRadius: "7px", height: 22,
                                bgcolor: isActive ? "#DCFCE7" : "#FEE2E2",
                                color:   isActive ? "#15803D" : "#B91C1C",
                                border: `1px solid ${isActive ? "#15803D33" : "#B91C1C33"}`,
                            }}
                        />
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMenuOpen(e, user.id); }}>
                            <MoreVertIcon sx={{ fontSize: 18, color: T.muted }} />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} open={menuOpen} onClose={onMenuClose}>
                            <MenuItem onClick={() => onStatusChange("Active")}   sx={{ fontSize: 13 }}>Mark Active</MenuItem>
                            <MenuItem onClick={() => onStatusChange("Inactive")} sx={{ fontSize: 13 }}>Mark Inactive</MenuItem>
                        </Menu>
                    </Box>
                </Box>

                {/* Name */}
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: T.text, mb: 1.2, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.2 }}>
                    {user.name}
                </Typography>

                {/* Contact */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <EmailOutlinedIcon sx={{ fontSize: 14, color: T.muted, flexShrink: 0 }} />
                        <Tooltip title={user.email}>
                            <Typography sx={{ fontSize: 12, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>
                                {user.email}
                            </Typography>
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PhoneOutlinedIcon sx={{ fontSize: 14, color: T.muted, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12, color: T.muted }}>{user.mobile_num}</Typography>
                    </Box>
                </Box>

                {/* Member since */}
                {user.createdAt && (
                    <Typography sx={{ fontSize: 11, color: T.muted, mt: 1.5 }}>
                        Member since {new Date(user.createdAt).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
                    </Typography>
                )}
            </CardContent>

            {/* Footer action */}
            <Box
                onClick={() => onViewDetails(user.id)}
                sx={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
                    py: 1.2, borderTop: `1px solid ${T.border}`,
                    cursor: "pointer", transition: "background 0.15s",
                    "&:hover": { bgcolor: T.tealLt },
                    borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px",
                }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.teal, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    View Details
                </Typography>
                <OpenInNewIcon sx={{ fontSize: 14, color: T.teal }} />
            </Box>
        </Card>
    );
};

// ── Skeleton card ─────────────────────────────────────────────────────────
const CardSkeleton = () => (
    <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, p: 2.5 }}>
        <Box sx={{ height: 3, bgcolor: T.border, borderRadius: 2, mb: 2 }} />
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Skeleton variant="circular" width={52} height={52} />
            <Box sx={{ flex: 1 }}>
                <Skeleton width="60%" height={20} sx={{ mb: 1 }} />
                <Skeleton width="40%" height={16} />
            </Box>
        </Box>
        <Skeleton width="80%" height={14} sx={{ mb: 0.8 }} />
        <Skeleton width="60%" height={14} />
    </Card>
);

// ── Main component ────────────────────────────────────────────────────────
export default function TruckOwners() {
    const [users,              setUsers]              = useState([]);
    const [loading,            setLoading]            = useState(true);
    const [error,              setError]              = useState(null);
    const [searchInput,        setSearchInput]        = useState("");
    const [appliedSearch,      setAppliedSearch]      = useState("");
    const [statusFilter,       setStatusFilter]       = useState("All");
    const [currentPage,        setCurrentPage]        = useState(0);
    const [perPage,            setPerPage]            = useState(12);
    const [menuAnchor,         setMenuAnchor]         = useState(null);
    const [selectedId,         setSelectedId]         = useState(null);
    const navigate = useNavigate();

    const fetchOwners = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await getData(API_ENDPOINTS.getTruckOwners);
            if (res.status === "SUCCESS" && res.result) {
                setUsers(res.result.map(o => ({
                    id:         o.owner_id,
                    name:       `${o.first_name || ""} ${o.last_name || ""}`.trim(),
                    email:      o.email || "N/A",
                    mobile_num: o.mobile_num || "N/A",
                    avatar:     o.pic || undefined,
                    status:     o.is_verified ? "Active" : "Inactive",
                    createdAt:  o.createdAt || "",
                })));
            } else {
                setError(res.message || "Failed to fetch truck owners.");
            }
        } catch (_) {
            setError("An error occurred while fetching data.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchOwners(); }, [fetchOwners]);

    // Clear search when input emptied
    useEffect(() => {
        if (searchInput === "" && appliedSearch !== "") {
            setAppliedSearch(""); setCurrentPage(0);
        }
    }, [searchInput, appliedSearch]);

    const activeCount   = users.filter(u => u.status === "Active").length;
    const inactiveCount = users.filter(u => u.status === "Inactive").length;

    const filtered = useMemo(() => {
        return users.filter(u => {
            const matchSearch = !appliedSearch ||
                u.name.toLowerCase().includes(appliedSearch.toLowerCase()) ||
                u.email.toLowerCase().includes(appliedSearch.toLowerCase()) ||
                u.mobile_num.toString().includes(appliedSearch);
            const matchStatus = statusFilter === "All" || u.status === statusFilter;
            return matchSearch && matchStatus;
        });
    }, [users, appliedSearch, statusFilter]);

    const totalPages   = Math.ceil(filtered.length / perPage);
    const paginated    = filtered.slice(currentPage * perPage, (currentPage + 1) * perPage);

    const handleSearch = (e) => {
        if (e.key === "Enter") { setAppliedSearch(searchInput); setCurrentPage(0); }
    };

    const handleFilterChange = (f) => { setStatusFilter(f); setCurrentPage(0); };

    const handleMenuOpen  = (e, id) => { setMenuAnchor(e.currentTarget); setSelectedId(id); };
    const handleMenuClose = ()       => { setMenuAnchor(null); setSelectedId(null); };

    const handleStatusChange = (newStatus) => {
        setUsers(prev => prev.map(u => u.id === selectedId ? { ...u, status: newStatus } : u));
        handleMenuClose();
    };

    const handleViewDetails = (id) => {
        navigate(`/app/owner?id=${encodeURIComponent(encryptId(id))}`);
    };

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
            }}>
                <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        Truck Owners
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
                        Manage and monitor all registered truck owners
                    </Typography>
                </Box>

                {/* Summary pills */}
                {!loading && (
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        <StatPill icon={<PeopleRoundedIcon />}       label="Total"    value={users.length}   color={T.teal}   bg="rgba(255,255,255,0.15)" />
                        <StatPill icon={<CheckCircleRoundedIcon />}   label="Active"   value={activeCount}    color="#15803D"  bg="rgba(220,252,231,0.9)" />
                        <StatPill icon={<CancelRoundedIcon />}        label="Inactive" value={inactiveCount}  color="#B91C1C"  bg="rgba(254,226,226,0.9)" />
                    </Box>
                )}
            </Box>

            {/* ── Toolbar ── */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2.5 }}>
                {/* Search */}
                <TextField
                    placeholder="Search by name, email or mobile…"
                    size="small"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={handleSearch}
                    sx={{ flex: "1 1 280px", maxWidth: 420 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: T.muted }} /></InputAdornment>,
                        endAdornment: searchInput ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => { setSearchInput(""); setAppliedSearch(""); setCurrentPage(0); }}>
                                    <ClearIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                        sx: { borderRadius: "12px", bgcolor: T.surface, fontSize: 13 },
                    }}
                />

                {/* Status filter pills */}
                <Box sx={{ display: "flex", gap: 1, bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", p: 0.5 }}>
                    {[
                        { key: "All",      count: users.length,   color: T.teal,    bg: T.tealLt   },
                        { key: "Active",   count: activeCount,    color: "#15803D", bg: "#DCFCE7"  },
                        { key: "Inactive", count: inactiveCount,  color: "#B91C1C", bg: "#FEE2E2"  },
                    ].map(f => (
                        <FilterBtn
                            key={f.key} label={f.key} active={statusFilter === f.key}
                            count={f.count} color={f.color} bg={f.bg}
                            onClick={() => handleFilterChange(f.key)}
                        />
                    ))}
                </Box>

                {/* Result count */}
                {!loading && (
                    <Typography sx={{ fontSize: 13, color: T.muted, ml: "auto" }}>
                        {filtered.length} owner{filtered.length !== 1 ? "s" : ""}
                    </Typography>
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

            {/* ── Grid ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 2, mb: 3 }}>
                {loading
                    ? Array.from({ length: perPage }).map((_, i) => <CardSkeleton key={i} />)
                    : paginated.length === 0
                    ? (
                        <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 8 }}>
                            <PeopleRoundedIcon sx={{ fontSize: 48, color: T.border, mb: 1 }} />
                            <Typography sx={{ color: T.muted, fontSize: 14 }}>No truck owners found.</Typography>
                        </Box>
                    )
                    : paginated.map(user => (
                        <OwnerCard
                            key={user.id}
                            user={user}
                            onViewDetails={handleViewDetails}
                            onMenuOpen={handleMenuOpen}
                            onMenuClose={handleMenuClose}
                            onStatusChange={handleStatusChange}
                            menuAnchor={menuAnchor}
                            menuOpen={Boolean(menuAnchor) && selectedId === user.id}
                        />
                    ))
                }
            </Box>

            {/* ── Pagination ── */}
            {!loading && filtered.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: "14px", px: 2.5, py: 1.5 }}>
                    <Typography sx={{ fontSize: 13, color: T.muted }}>
                        Showing {Math.min(currentPage * perPage + 1, filtered.length)}–{Math.min((currentPage + 1) * perPage, filtered.length)} of {filtered.length}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setCurrentPage(0); }} size="small"
                            sx={{ borderRadius: "10px", fontSize: 13 }}>
                            {[12, 24, 48].map(n => <MenuItem key={n} value={n}>{n} per page</MenuItem>)}
                        </Select>

                        <IconButton size="small" onClick={() => setCurrentPage(p => Math.max(p - 1, 0))} disabled={currentPage === 0}
                            sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                            <KeyboardArrowLeft sx={{ fontSize: 18 }} />
                        </IconButton>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, minWidth: 60, textAlign: "center" }}>
                            {currentPage + 1} / {totalPages}
                        </Typography>
                        <IconButton size="small" onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages - 1))} disabled={currentPage >= totalPages - 1}
                            sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                            <KeyboardArrowRight sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
